import { useMutation } from "@apollo/client/react"; import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Grid, Button } from "@mui/material";
import CardBox from "../../CardBox/CardBox";
import { RenderInputByTypeMobile } from "../../../../../../components/formGroupGenerator/RenderInputByTypeMobile";
import { useVisita } from "../../../../../../context/VisitaContext";
import { useNavigate } from "react-router-dom";
import SAVE_VISITA_FIRMA, { adaptAddVisitaFirmaInput } from "../../../../../../data/graphQL/mutations/Visitas/saveVisitaFirma";
import { ID_TIPO_VISITA_TELEENFERMERIA } from "../../../MisVisitasCreationContent";
import toast from "react-hot-toast";
import VisitaFirmaInput from "../../VisitaFirmaInput/VisitaFirmaInput";
import { useStepSave } from "../../../../hooks/useStepSave";
import { validateStepFields } from "../../../../utils/visitaStepValidation";
import { obtenerCoordenadas } from "../../../../constants/getUbicacion";
import { useValidacionDistanciaVisita } from "../../../../hooks/useValidacionDistanciaVisita";
import { useValidacionDiscrepanciaHorariaVisita } from "../../../../hooks/useValidacionDiscrepanciaHorariaVisita";

const ID_INTERLOCUTOR_PACIENTE = 10;

export default function FirmaPacienteFamiliar({ readOnly = false, onExposeFormatted, exposedFormattedByStep, onSaveRef }) {
  const navigate = useNavigate();
  const { validarDistancia, modal: modalDistancia } = useValidacionDistanciaVisita();
  const { validarDiscrepancia, modal: modalDiscrepancia } = useValidacionDiscrepanciaHorariaVisita();

  const [formData, setFormData] = useState({});
  const [finalizarLoading, setFinalizarLoading] = useState(false);

  const { visita, setVisita } = useVisita();

  const [save] = useMutation(SAVE_VISITA_FIRMA);

  const formattedInputs = useMemo(() => {
    return firmaPacienteFamiliarInputs.map((input) => {
      const cloned = { ...input };

      switch (input.name) {
        case "firmaPaciente":
          cloned.required = !!visita.conformidad;
          break;
      }

      if (readOnly) cloned.disabled = true;

      return cloned;
    });
  }, [readOnly, visita.conformidad]);

  const handleChange = useCallback((name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));

    if (name === 'idComboVisitaValorInterlocutor')
      setFormData((prev) => ({ ...prev, nombreCompleto: ID_INTERLOCUTOR_PACIENTE == value ? visita?.paciente : null }));
  }, []);

  const saveVisitaFirma = async (visitaData, finalizar) => {
    const coordenadas = await obtenerCoordenadas();

    const { data } = await save({
      variables: { input: adaptAddVisitaFirmaInput(visitaData, finalizar, coordenadas?.latitud, coordenadas?.longitud) }
    });

    const idVisitaResult = data?.saveVisitaFirma?.idVisita;
    if (!idVisitaResult) throw new Error("Error al guardar la visita");

    return idVisitaResult;
  };

  useStepSave(onSaveRef, async () => {
    const updatedVisita = { ...visita, ...formData };
    await saveVisitaFirma(updatedVisita);
    setVisita(updatedVisita);
  });

  async function tieneGeoHabilitada() {
    return new Promise((resolve) => {
      if (!("geolocation" in navigator)) {
        resolve(false);
        return;
      }

      const timeoutMs = 60000;

      // Intenta obtener la ubicación con un timeout
      const timeoutId = setTimeout(() => {
        resolve(false);
      }, timeoutMs);

      navigator.geolocation.getCurrentPosition(
        () => {
          clearTimeout(timeoutId);
          resolve(true);
        },
        (error) => {
          clearTimeout(timeoutId);
          // Si el error es PERMISSION_DENIED, no tiene permisos
          // Si es POSITION_UNAVAILABLE o TIMEOUT, puede tener permisos pero hay otros problemas
          if (error.code === error.PERMISSION_DENIED) {
            resolve(false);
          } else {
            // Asumimos que tiene permisos pero hay problemas técnicos
            resolve(true);
          }
        },
        {
          timeout: timeoutMs,
          maximumAge: 0,
          enableHighAccuracy: false // Más rápido para la verificación
        }
      );
    });
  }

  const handleGuardar = async () => {
    try {
      const tieneGeo = await tieneGeoHabilitada();

      if (!tieneGeo) {
        toast.error("Debe habilitar el permiso de ubicación en su navegador para crear una nueva visita.", {
          duration: 4000
        });
        return;
      }

      setFinalizarLoading(true);
      const email = formData?.email || "";
      if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        toast.error("El email ingresado no es válido.");
        return;
      }

      validateStepFields({ formattedInputs, formData });

      const updatedVisita = { ...visita, ...formData };

      const resValidacionDistancia = await validarDistancia(updatedVisita.idVisita, { omitirValidacion: updatedVisita?.idComboVisitaValorTipoVisita === ID_TIPO_VISITA_TELEENFERMERIA });
      if (!resValidacionDistancia.permitir) return;

      const resValidacionDiscrepancia = await validarDiscrepancia(updatedVisita.idVisita, updatedVisita.fechaVisita, updatedVisita.fechaFin);
      if (!resValidacionDiscrepancia.permitir) return;

      await saveVisitaFirma(updatedVisita, true);
      setVisita(updatedVisita);

      toast.success("Visita guardada correctamente.");
      navigate("/misVisitas");
    } catch (error) {
      console.error(error);
    } finally {
      setFinalizarLoading(false);
    }
  };

  useEffect(() => {
    setFormData(prev => ({
      ...prev,
      ...visita,
      idComboVisitaValorInterlocutor: prev.idComboVisitaValorInterlocutor || visita.idComboVisitaValorInterlocutor,
      nombreCompleto: prev.nombreCompleto || visita.nombreCompleto,
      email: prev.email || visita.email
    }));
  }, [visita]);

  return (
    <CardBox>
      <Grid container spacing={2}>
        {formattedInputs.map((input) =>
          <Grid size={{ xs: 12 }} key={input.name}>
            {input.name !== 'firmaPaciente' && <RenderInputByTypeMobile
              key={`${input.name} - ${input.name === 'nombreCompleto' ? formData['idComboVisitaValorInterlocutor'] : ''}`}
              label={input.displayName}
              value={formData[input.name]}
              handleChange={handleChange}
              input={input} />
            }
            {input.name === 'firmaPaciente' && !!visita.conformidad && <VisitaFirmaInput
              key={'firmaPaciente' + input.name}
              idVisita={visita.idVisita}
              handleChange={handleChange}
              inputName={input.name}
              readOnly={readOnly} />
            }
          </Grid>
        )}
        <Grid size={{ xs: 12 }}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleGuardar}
            disabled={readOnly}
            fullWidth
            loading={finalizarLoading}>

            Finalizar
          </Button>
        </Grid>
      </Grid>
      {modalDistancia}
      {modalDiscrepancia}
    </CardBox>);

}

const firmaPacienteFamiliarInputs = [
  {
    name: "idComboVisitaValorInterlocutor",
    displayName: "Interlocutor",
    type: "select",
    required: true,
    options: [
      { id: 10, displayName: "Paciente" },
      { id: 11, displayName: "Familia" },
      { id: 12, displayName: "Cuidador" }]

  },
  {
    name: "nombreCompleto",
    displayName: "Nombre",
    type: "string",
    maxLength: 250,
    required: true
  },
  {
    name: "firmaPaciente",
    displayName: "Firma Paciente / Familiar",
    type: "signature",
    required: true
  },
  {
    name: "email",
    displayName: "Email paciente / Interlocutor",
    type: "string",
    maxLength: 250,
    required: false
  }];